import { useTranslation } from 'react-i18next'

import { fontSizes } from '@enechain/ecloud-designsystem'

import { eclColor } from '~/pages/AdminApprovalFlow/constants/eclColor'
import type {
  AutoBidEffective,
  AutoBidPriceRange,
} from '~/pages/AdminApprovalFlow/models/autoBid'

/**
 * 価格入力欄の下に出す「許容範囲 {min}〜{max} 円/kWh (設定＞入札)」と価格の実効値
 * (AC-APF-043)。 範囲内でも常時表示し、 clamp・JEPX 値域外・成行不可の指値変換で
 * 警告色にする。 `effective` が undefined (時間前の動的基準) の時は価格の実効値を
 * 出さず、 上下限だけを出す。
 */
export const AutoBidPriceRangeHint = ({
  effective,
  priceRange,
}: {
  effective: AutoBidEffective | undefined
  priceRange: AutoBidPriceRange
}): React.ReactElement => {
  const { t } = useTranslation()
  const formatBound = (v: number | undefined): string =>
    v !== undefined
      ? v.toFixed(2)
      : t('page:adminApprovalFlow.autoBid.modal.priceRangeUnlimited')
  const range = t('page:adminApprovalFlow.autoBid.modal.priceRangeFormat', {
    min: formatBound(priceRange.min),
    max: formatBound(priceRange.max),
  })
  const warning = effective?.warning ?? false

  return (
    <div
      data-warning={warning || undefined}
      style={{
        fontSize: fontSizes.sm.value,
        // 文字なので textWarning (= Text.warningHigh)。
        color: warning ? eclColor.textWarning : eclColor.textMid,
        fontWeight: warning ? 600 : 400,
      }}
    >
      {effective === undefined
        ? range
        : `${range} ${autoBidPriceEffectiveText(t, effective)}`}
    </div>
  )
}

/** 価格の実効値の文言。 JEPX 値域外は「→ 入札価格」に代えて専用文言を出す (AC-APF-043)。 */
export function autoBidPriceEffectiveText(
  t: ReturnType<typeof useTranslation>['t'],
  effective: AutoBidEffective,
): string {
  return effective.outsideJepx
    ? t('page:adminApprovalFlow.autoBid.outsideJepx')
    : t('page:adminApprovalFlow.autoBid.priceEffectiveArrow', {
        value: effective.effectivePrice?.toFixed(2) ?? '',
      })
}
